import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, TruckIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Card,
  CompactFilterBar,
  ContentLoader,
  EmptyState,
  Modal,
  Pagination,
  Table,
  getStatusBadgeVariant,
} from '@/components/common';
import { policyNumberLabel } from '@/components/common/PolicyNumber';
import { InsurerLogo } from '@/components/common/InsurerLogo';
import type { Column, SortDirection } from '@/components/common/Table';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams } from '@/hooks/useFilterParams';
import { useVehicleDatabase, useVehicleDatabaseEntry } from '@/hooks';
import type { VehicleDatabaseOrdering, VehicleDatabaseRow } from '@/services/vehicleDatabase';
import { formatCurrency, formatDate, titleCase } from '@/utils';

/**
 * The broker's Vehicle Database — vehicle-number-wise, not master data.
 *
 * This menu used to lead to five catalogue lists (Makes, Models, Variants,
 * Fuel Types, Body Types). Those describe what vehicles EXIST; a broker
 * opening "Vehicle Database" wants to know what we know about one vehicle.
 *
 * The rows are the broker's own: vehicles on their policies and on their
 * customers' records. The global registration registry is deliberately not the
 * source — it has no broker column, so listing it would show every broker
 * everyone else's book.
 */

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * Module-level, because `useFilterParams` reads it once to learn both the keys
 * it owns and each key's shape (every one here is a plain string).
 *
 * DELIBERATELY NOT HERE: the page offset. Every filter change restarts the
 * list from the top, so an offset restored from the URL would be clobbered
 * before first paint.
 */
type VehiclePanelFilters = {
  search: string;
  /** B10: `'true'` narrows to vehicles this broker actually holds. */
  mine: string;
  /** U-04: the server ordering field, '' when the list is in its default order. */
  sort: string;
  sort_dir: string;
};

const EMPTY_FILTERS: VehiclePanelFilters = {
  search: '',
  mine: '',
  sort: '',
  sort_dir: '',
};

/**
 * The house namespace for a page's own filters.
 *
 * This page honours NO unprefixed deep-link params -- nothing links into
 * `/vehicle-database?…`, it is reached from the sidebar. So there is no seed to
 * consume and no `DEEP_LINK_PARAMS` list. The prefix is kept anyway so that the
 * day such a link is added, the user's own filters are already in a namespace
 * that cannot be mistaken for it.
 */
const FILTER_PREFIX = 'f_';

/**
 * U-04: the ONLY `?ordering=` values the server understands --
 * `VehicleDatabaseView.ORDERINGS` in backend/apps/vehicles/views.py:1222.
 *
 * This endpoint is an `APIView` that assembles its rows in Python rather than
 * a DRF viewset with an `OrderingFilter`, so every value has to be
 * hand-supported there; an unknown one is simply ignored, which is how a sort
 * header that does nothing ships unnoticed. Only a column whose key is in this
 * set is marked `sortable`.
 */
const SORTABLE_FIELDS = new Set(['registration_date']);

/**
 * B-01: the server's page size -- `VehicleDatabaseView.PAGE_SIZE` in
 * backend/apps/vehicles/views.py. This endpoint pages by `?offset=`, not by
 * `?page=`, and it does not echo the size back, so the two numbers have to
 * agree by hand: the page count below is derived from it, and a mismatch would
 * draw a pager that offers pages the server never returns.
 */
const PAGE_SIZE = 50;

export function VehicleDatabasePage() {
  const [openVehicle, setOpenVehicle] = useState<string | null>(null);

  // Q-01: restored SYNCHRONOUSLY in the hook's lazy initializer, so the first
  // render -- and the first request -- is already filtered and sorted, and the
  // filter panel opens over a filtered list rather than hiding the reason for
  // it.
  const { filters, setFilter, setFilters, clearAll } = useFilterParams<VehiclePanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
  });

  // Search-as-you-type without a request per keystroke. Seeded from the
  // restored `?f_search=` so a refresh brings the box back filled in, not just
  // the results.
  const { term, debouncedTerm, setTerm } = useDebouncedSearch(filters.search);

  /**
   * B-01: how far into the list we are. The endpoint is offset-based, so this
   * is a row offset rather than a page number.
   *
   * DELIBERATELY NOT IN `VehiclePanelFilters`, for the reason recorded there:
   * every filter change restarts the list from the top, so an offset restored
   * from the URL would be clobbered before first paint. It is reset at each of
   * the four places that can change what the list contains -- the search push
   * below, the "my book" toggle, the sort, and Clear -- which is the same
   * shape `PoliciesListPage` uses for its `page`.
   */
  const [offset, setOffset] = useState(0);

  // Push the debounced term into the filters. Keyed off a ref of the LAST TERM
  // PUSHED rather than off `filters.search`: seeded with the restored value it
  // makes the mount pass a no-op, and it stops Clear being undone by the 300ms
  // of stale `debouncedTerm` still in flight behind it.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilter('search', debouncedTerm);
    setOffset(0);
  }, [debouncedTerm, setFilter]);

  const mineOnly = filters.mine === 'true';

  // U-04: only a field the server can order by, so a stale or hand-edited
  // `?f_sort=` cannot leave a header drawn as sorted over a list that is not.
  const sortKey = SORTABLE_FIELDS.has(filters.sort) ? filters.sort : '';
  const sortDirection: SortDirection = filters.sort_dir === 'desc' ? 'desc' : 'asc';
  const ordering: VehicleDatabaseOrdering | undefined = sortKey
    ? ((sortDirection === 'desc' ? `-${sortKey}` : sortKey) as VehicleDatabaseOrdering)
    : undefined;

  // The QUERY reads `filters.search`, not `debouncedTerm`: Clear resets the
  // filter synchronously while the debounced term still holds the old text for
  // another 300ms, so querying on the latter would keep the search the user
  // just cleared alive for one more request.
  const { data, isLoading, isError, error, refetch } = useVehicleDatabase({
    search: filters.search || undefined,
    mine: mineOnly,
    ordering,
    offset,
  });
  const rows = useMemo(() => data?.results ?? [], [data]);

  // B-01: `count` is the whole FILTERED set, not this slice of it -- see the
  // comment on it in backend/apps/vehicles/views.py.
  const totalItems = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  /**
   * U-04: the shared Table's sorting, in place of the bespoke header button
   * this page used to own.
   *
   * ONE DELIBERATE DIFFERENCE from the Policies pilot: the cycle here is
   * ascending -> descending -> OFF, not a straight asc/desc toggle. The
   * server's default order is alphabetical by registration number and is NOT
   * one of its `ORDERINGS`, so without a third click there is no way back to
   * it -- dropping that would silently delete a capability this page already
   * shipped. `Table` only ever offers 'asc' or 'desc', so the third click
   * arrives here as ('registration_date', 'asc') while the column is already
   * descending, which is unambiguous and is what this reads.
   */
  const handleSortChange = (key: string, direction: SortDirection) => {
    // B-01: reordering the whole book makes offset 50 a different fifty rows,
    // so the list restarts at the top -- the same rule every filter follows.
    setOffset(0);
    if (filters.sort === key && filters.sort_dir === 'desc' && direction === 'asc') {
      setFilters({ sort: '', sort_dir: '' });
      return;
    }
    setFilters({ sort: key, sort_dir: direction });
  };

  // The PANEL's own controls only: the search box is always visible, and the
  // sort is a view preference rather than a filter, so neither badges.
  const activeFilterCount = mineOnly ? 1 : 0;

  const clearFilters = () => {
    setTerm('');
    clearAll();
    setOffset(0);
  };

  /**
   * U-02: the columns, each sized as CONTENT + CELL PADDING.
   *
   * `Table` pads every `<td>` with `px-6` (48px) and, under `layout="fixed"`,
   * sets `truncate` -- `overflow: hidden`, with no scrollbar and no ellipsis on
   * an atomic inline box. A column declared at 7rem therefore offers 64px of
   * content, not 112px, and anything wider is CLIPPED. The four narrow columns
   * override the padding to `px-3` (24px) through `className` (`cn` is
   * tailwind-merge, so a `px-*` there beats the `px-6`):
   *
   *   registration_number  11rem  px-6 -> 128px (header "VEHICLE NUMBER" ~115px)
   *   make_model           12rem  px-6 -> 144px
   *   variant_name         10rem  px-6 -> 112px
   *   manufacturing_year    6rem  px-3 ->  72px
   *   engine_chassis       12rem  px-6 -> 144px (header ~125px)
   *   registration_date     9rem  px-3 -> 120px (header + sort icon)
   *   customer_name        11rem  px-6 -> 128px
   *   in_your_book          9rem  px-3 -> 120px (header "IN YOUR BOOK" ~100px)
   *                       -------
   *                        80rem = 1280px, inside the ~1424px a 1440-1536px
   *   desktop shows without a horizontal PAGE scrollbar. Narrower than that the
   *   table -- not the page -- scrolls sideways inside its own card.
   *
   * Every cell here renders JSX, so every one carries its OWN `title`:
   * `Table` sets a tooltip only for content that is already a string or a
   * number. The "Yours"/"Trail only" badge additionally gets `max-w-full` with
   * its label in a `truncate` span -- an `inline-flex` badge is an atomic
   * inline-level box that a `truncate` ancestor hard-CLIPS rather than
   * ellipsises.
   */
  const columns: Column<VehicleDatabaseRow>[] = [
    {
      key: 'registration_number',
      header: 'Vehicle Number',
      width: '11rem',
      render: (row) => (
        <div className="min-w-0">
          <p
            className="truncate text-sm font-medium text-gray-900 dark:text-white"
            title={row.registration_number}
          >
            {row.registration_number}
          </p>
          {row.fuel_type && (
            <p
              className="truncate text-xs text-gray-500 dark:text-gray-400"
              title={titleCase(row.fuel_type)}
            >
              {titleCase(row.fuel_type)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'make_model',
      header: 'Make & Model',
      width: '12rem',
      render: (row) => (
        <div className="min-w-0">
          <p
            className="truncate text-sm text-gray-900 dark:text-white"
            title={row.make_name || '—'}
          >
            {row.make_name || '—'}
          </p>
          <p
            className="truncate text-xs text-gray-500 dark:text-gray-400"
            title={row.model_name || '—'}
          >
            {row.model_name || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'variant_name',
      header: 'Variant',
      width: '10rem',
      render: (row) => (
        <p
          className="truncate text-sm text-gray-700 dark:text-gray-300"
          title={row.variant_name || '—'}
        >
          {row.variant_name || '—'}
        </p>
      ),
    },
    {
      key: 'manufacturing_year',
      header: 'Year',
      width: '6rem',
      className: 'px-3',
      render: (row) => (
        <p className="truncate text-sm text-gray-700 dark:text-gray-300">
          {row.manufacturing_year ?? '—'}
        </p>
      ),
    },
    // B-04: two columns, not one stacked cell -- the owner scans and copies
    // these one identifier at a time, and a stacked pair read as one value.
    {
      key: 'engine_number',
      header: 'Engine No.',
      width: '10rem',
      render: (row) => (
        <p
          className="truncate font-mono text-sm text-gray-700 dark:text-gray-300"
          title={row.engine_number || '—'}
        >
          {row.engine_number || '—'}
        </p>
      ),
    },
    {
      key: 'chassis_number',
      header: 'Chassis No.',
      width: '11rem',
      render: (row) => (
        <p
          className="truncate font-mono text-sm text-gray-700 dark:text-gray-300"
          title={row.chassis_number || '—'}
        >
          {row.chassis_number || '—'}
        </p>
      ),
    },
    {
      // B10: real server-side ordering (`?ordering=registration_date`), not a
      // client-side re-sort of whatever page happened to load -- the backend
      // proof lives in `backend/tests/test_vehicle_database.py`.
      //
      // The state is announced by the `aria-sort` the shared Table puts on the
      // `<th>`, which is the standard ARIA mechanism for a sortable column
      // header. The bespoke button this replaced carried an `aria-label`
      // instead, which folds the state into the button's own accessible NAME --
      // so it changed as the user toggled and was announced twice.
      key: 'registration_date',
      header: 'Registered',
      width: '9rem',
      className: 'px-3',
      sortable: true,
      render: (row) => {
        const label = row.registration_date ? formatDate(row.registration_date) : '—';
        return (
          <p className="truncate text-sm text-gray-700 dark:text-gray-300" title={label}>
            {label}
          </p>
        );
      },
    },
    {
      key: 'customer_name',
      header: 'Customer',
      width: '11rem',
      render: (row) => (
        <p
          className="truncate text-sm text-gray-700 dark:text-gray-300"
          title={row.customer_name || '—'}
        >
          {row.customer_name || '—'}
        </p>
      ),
    },
    {
      // The list is common to every broker, so a row has to say whether it is
      // actually theirs. Without this a shared vehicle reads as one of their own.
      key: 'in_your_book',
      header: 'In your book',
      width: '9rem',
      className: 'px-3',
      render: (row) => (
        <Badge variant={row.in_your_book ? 'success' : 'default'} size="sm" className="max-w-full">
          <span className="truncate" title={row.in_your_book ? 'Yours' : 'Trail only'}>
            {row.in_your_book ? 'Yours' : 'Trail only'}
          </span>
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Vehicle Database"
        subtitle={
          data
            ? `${data.count} vehicle${data.count === 1 ? '' : 's'} — ${mineOnly ? 'your own only' : 'your own, and the shared trail'}`
            : 'Vehicles by number'
        }
        breadcrumbs={[{ label: 'Vehicle Database' }]}
      />

      <CompactFilterBar
        search={term}
        onSearchChange={setTerm}
        searchPlaceholder="Search by vehicle number, e.g. MH01AB1234"
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
      >
        {/* B10: the "Yours"/"Trail only" badge as a real filter toggle, rather
            than only a label on each row. It lives in the panel, so it is what
            the Filter badge counts. */}
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none sm:self-end sm:pb-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
            checked={mineOnly}
            onChange={(e) => {
              setFilter('mine', e.target.checked ? 'true' : '');
              // B-01: narrowing the book invalidates the offset -- page 3 of
              // everything is past the end of "only mine".
              setOffset(0);
            }}
          />
          Show only vehicles in my book
        </label>
      </CompactFilterBar>

      {/* Three states, not two. A failed request rendered as an empty list
          tells the broker they have no vehicles, which is a different and
          much worse answer than "we could not load them". */}
      {isLoading ? (
        <Table<VehicleDatabaseRow>
          data={[]}
          columns={columns}
          isLoading
          rowKey={(row) => row.registration_number}
        />
      ) : isError ? (
        <Card padding="none">
          <EmptyState
            icon={<ExclamationTriangleIcon />}
            title="Could not load your vehicles"
            description={
              error instanceof Error
                ? error.message
                : 'The request failed. This does not mean you have no vehicles.'
            }
            action={
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Try again
              </button>
            }
          />
        </Card>
      ) : rows.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={<TruckIcon />}
            title={filters.search ? 'No vehicle matches that number' : 'No vehicles yet'}
            description={
              filters.search
                ? 'Nothing matches that number — not in your own book, and not in the shared vehicle trail.'
                : 'Vehicles appear here as you issue policies and add vehicles to your customers, alongside any vehicle in the shared trail.'
            }
          />
        </Card>
      ) : (
        <>
          <Table<VehicleDatabaseRow>
            data={rows}
            columns={columns}
            rowKey={(row) => row.registration_number}
            onRowClick={(row) => setOpenVehicle(row.registration_number)}
            // U-02: declared widths (no column jump when a filter changes which
            // rows are on screen) and a header that stays put while they scroll.
            layout="fixed"
            stickyHeader
            // U-04: the Table never reorders anything -- it reports the next sort
            // state and the server does the ordering.
            sortKey={sortKey || undefined}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
          {/* B-01: this page had NO pager at all, and the endpoint returns 50
              rows a request -- so a broker with 7,000 vehicles could reach the
              first 50 of them and no more. */}
          {totalPages > 1 && (
            <Card padding="none" className="mt-4 overflow-hidden">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={PAGE_SIZE}
                onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
              />
            </Card>
          )}
        </>
      )}

      <VehicleHistoryModal registrationNumber={openVehicle} onClose={() => setOpenVehicle(null)} />
    </div>
  );
}

/** One vehicle's full record: our policies, plus any bought history. */
function VehicleHistoryModal({
  registrationNumber,
  onClose,
}: {
  registrationNumber: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error } = useVehicleDatabaseEntry(registrationNumber);

  if (!registrationNumber) return null;

  return (
    <Modal isOpen onClose={onClose} title={registrationNumber} size="lg">
      {isLoading ? (
        <ContentLoader />
      ) : isError ? (
        <p className="text-sm text-red-700 dark:text-red-400">
          {error instanceof Error ? error.message : 'Could not load this vehicle.'}
        </p>
      ) : !data ? null : (
        <div className="space-y-6">
          <section>
            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Vehicle</h4>
            <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <Field label="Make" value={data.make_name} />
              <Field label="Model" value={data.model_name} />
              <Field label="Variant" value={data.variant_name} />
              <Field label="Fuel Type" value={data.fuel_type && titleCase(data.fuel_type)} />
              <Field
                label="Manufacturing Year"
                value={data.manufacturing_year ? String(data.manufacturing_year) : ''}
              />
              <Field label="Chassis Number" value={data.chassis_number} />
              <Field label="Engine Number" value={data.engine_number} />
              <Field
                label="Registration Date"
                value={data.registration_date ? formatDate(data.registration_date) : ''}
              />
              <Field label="Customer" value={data.customer_name} />
            </div>
          </section>

          <section>
            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              Your policies on this vehicle
            </h4>
            {/* "None" here means none of YOURS. The vehicle may well be
                insured by somebody else — that is what the trail below shows —
                and saying "none yet" flatly would read as uninsured. */}
            {data.our_policies.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                None. This vehicle is in the shared trail; you have not written a policy on it.
              </p>
            ) : (
              <ul className="space-y-2">
                {data.our_policies.map((policy) => (
                  <li
                    key={policy.id}
                    className="flex items-center justify-between gap-4 rounded-md border border-gray-100 px-3 py-2 dark:border-gray-700"
                  >
                    {/* U-05: the insurer's logo leads the row. */}
                    <div className="flex min-w-0 items-center gap-3">
                      <InsurerLogo
                        src={policy.insurer_logo_url}
                        name={policy.insurer_name}
                        description={policy.insurer_description}
                        size="sm"
                      />
                      <div className="min-w-0">
                        <Link
                          to={`/policies/${policy.id}`}
                          onClick={onClose}
                          className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {policyNumberLabel(policy)}
                        </Link>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {policy.insurer_name || 'Insurer not recorded'}
                          {policy.expiry_date ? ` · expires ${formatDate(policy.expiry_date)}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {formatCurrency(policy.total_premium)}
                      </span>
                      <Badge variant={getStatusBadgeVariant(policy.status)} size="sm">
                        {titleCase(policy.status)}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              Insurance history
            </h4>
            {/* The distinction matters: an empty list because nothing was ever
                bought is not the same as "this vehicle has never been
                insured", and saying the latter would be a lie. */}
            {!data.in_registry ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No registration lookup has been bought for this vehicle, so no history outside your
                own policies is known.
              </p>
            ) : data.insurance_history.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                The lookup returned no previous insurance for this vehicle.
              </p>
            ) : (
              <ul className="space-y-2">
                {data.insurance_history.map((record, index) => (
                  <li
                    key={`${record.policy_number}-${index}`}
                    className="flex items-center justify-between gap-4 rounded-md border border-gray-100 px-3 py-2 dark:border-gray-700"
                  >
                    {/* U-05: a logo ONLY when the vendor's insurer text matched
                        one of ours -- an unmatched row keeps its plain text
                        rather than wearing a generic icon that implies a match. */}
                    <div className="flex min-w-0 items-center gap-3">
                      {record.insurer_logo_url && (
                        <InsurerLogo
                          src={record.insurer_logo_url}
                          name={record.insurer_name}
                          description={record.insurer_description}
                          size="sm"
                        />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {record.insurer_name || 'Insurer not recorded'}
                        </p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {record.policy_number || 'Policy number not recorded'}
                          {record.valid_until ? ` · to ${formatDate(record.valid_until)}` : ''}
                        </p>
                      </div>
                    </div>
                    <Badge variant={record.placed_by_you ? 'success' : 'default'} size="sm">
                      {record.placed_by_you ? 'Placed by you' : 'Placed elsewhere'}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {data.ownership_history.length > 0 && (
            <section>
              <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                Ownership history
              </h4>
              <ul className="space-y-2">
                {data.ownership_history.map((record, index) => (
                  <li
                    key={`${record.owner_name}-${index}`}
                    className="rounded-md border border-gray-100 px-3 py-2 dark:border-gray-700"
                  >
                    <p className="text-sm text-gray-900 dark:text-white">{record.owner_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {record.owner_serial_number
                        ? `Owner ${record.owner_serial_number}`
                        : 'Owner number not recorded'}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm text-gray-900 dark:text-white">{value || '—'}</p>
    </div>
  );
}
